import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { graphqlRequest } from '../../service/graphql-client';
import { GQ_LOGIN, GQ_LOGOUT, GQ_REFRESH_TOKEN, GQ_UPDATE_USER_PROFILE, GQ_ME_QUERY } from '../../service/graphql-mutations';

// Initial state
const initialState = {
  user: null,
  accessToken: null,
  refreshToken: null,
  isAuthenticated: false,
  isLoading: false,
  error: null,
};

// Async thunks
export const loginUser = createAsyncThunk(
  'auth/loginUser',
  async (credentials, { rejectWithValue }) => {
    try {
      const data = await graphqlRequest(GQ_LOGIN, {
        username: credentials.username || credentials.email,
        password: credentials.password
      });

      if (data.login && data.login.token) {
        const token = data.login.token;
        return {
          user: token.user,
          access_token: token.accessToken,
          refresh_token: token.refreshToken
        };
      } else {
        return rejectWithValue('Login failed');
      }
    } catch (error) {
      return rejectWithValue(error.message || 'Login failed. Please try again.');
    }
  }
);

export const logoutUser = createAsyncThunk(
  'auth/logoutUser',
  async (_, { getState }) => {
    try {
      const state = getState();
      const accessToken = state.auth.accessToken;
      
      if (accessToken) {
        await graphqlRequest(GQ_LOGOUT, {}, accessToken);
      }
      return true;
    } catch (error) {
      // Even if API call fails, we still want to logout locally
      return true;
    }
  }
);

export const getCurrentUser = createAsyncThunk(
  'auth/getCurrentUser',
  async (_, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const accessToken = state.auth.accessToken;
      
      if (!accessToken) {
        return rejectWithValue('Not authenticated');
      }

      const data = await graphqlRequest(GQ_ME_QUERY, {}, accessToken);

      if (data.me) {
        return data.me;
      } else {
        return rejectWithValue('Failed to fetch user');
      }
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to fetch user');
    }
  }
);

export const refreshToken = createAsyncThunk(
  'auth/refreshToken',
  async (_, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const refreshToken = state.auth.refreshToken;
      
      if (!refreshToken) {
        return rejectWithValue('No refresh token available');
      }

      const data = await graphqlRequest(GQ_REFRESH_TOKEN, {
        refreshToken: refreshToken
      });

      if (data.refreshToken && data.refreshToken.token) {
        const token = data.refreshToken.token;
        return {
          user: token.user,
          access_token: token.accessToken,
          refresh_token: token.refreshToken
        };
      } else {
        return rejectWithValue('Failed to refresh token');
      }
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to refresh token');
    }
  }
);

export const updateUserProfile = createAsyncThunk(
  'auth/updateUserProfile',
  async (profileData, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const accessToken = state.auth.accessToken;
      
      if (!accessToken) {
        return rejectWithValue('You must be logged in to update your profile.');
      }

      const data = await graphqlRequest(GQ_UPDATE_USER_PROFILE, {
        firstName: profileData.firstName,
        lastName: profileData.lastName,
        phoneNumber: profileData.phone
      }, accessToken);

      if (data.updateUserProfile && data.updateUserProfile.success) {
        // Return the profile data to update the user in the store (using camelCase to match GraphQL schema)
        return {
          firstName: profileData.firstName,
          lastName: profileData.lastName,
          phoneNumber: profileData.phone
        };
      } else {
        return rejectWithValue('Profile update failed');
      }
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to update profile. Please try again.');
    }
  }
);

// Auth slice
const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setLoading: (state, action) => {
      state.isLoading = action.payload;
    },
    // For cases where we need to manually set auth state (like from localStorage on app init)
    setAuthState: (state, action) => {
      const { user, accessToken, refreshToken } = action.payload;
      state.user = user;
      state.accessToken = accessToken;
      state.refreshToken = refreshToken;
      state.isAuthenticated = !!accessToken;
    },
    clearAuthState: (state) => {
      state.user = null;
      state.accessToken = null;
      state.refreshToken = null;
      state.isAuthenticated = false;
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    builder
      // Login
      .addCase(loginUser.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.isLoading = false;
        state.user = action.payload.user;
        state.accessToken = action.payload.access_token;
        state.refreshToken = action.payload.refresh_token;
        state.isAuthenticated = true;
        state.error = null;
        // Store user in localStorage for persistence
        if (action.payload.user) {
          localStorage.setItem('user', JSON.stringify(action.payload.user));
        }
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
        state.isAuthenticated = false;
      })
      // Logout
      .addCase(logoutUser.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(logoutUser.fulfilled, (state) => {
        state.isLoading = false;
        state.user = null;
        state.accessToken = null;
        state.refreshToken = null;
        state.isAuthenticated = false;
        state.error = null;
        // Clear localStorage
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        localStorage.removeItem('user');
        localStorage.removeItem('remember_me');
      })
      .addCase(logoutUser.rejected, (state) => {
        // Even if logout API fails, clear local state
        state.isLoading = false;
        state.user = null;
        state.accessToken = null;
        state.refreshToken = null;
        state.isAuthenticated = false;
        state.error = null;
        // Clear localStorage
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        localStorage.removeItem('user');
        localStorage.removeItem('remember_me');
      })
      // Get Current User
      .addCase(getCurrentUser.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getCurrentUser.fulfilled, (state, action) => {
        state.isLoading = false;
        state.user = action.payload;
        state.isAuthenticated = true;
        state.error = null;
      })
      .addCase(getCurrentUser.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
        state.isAuthenticated = false;
      })
      // Refresh Token
      .addCase(refreshToken.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(refreshToken.fulfilled, (state, action) => {
        state.isLoading = false;
        state.user = action.payload.user;
        state.accessToken = action.payload.access_token;
        state.refreshToken = action.payload.refresh_token;
        state.isAuthenticated = true;
        state.error = null;
        // Store tokens in localStorage
        localStorage.setItem('access_token', action.payload.access_token);
        localStorage.setItem('refresh_token', action.payload.refresh_token);
        if (action.payload.user) {
          localStorage.setItem('user', JSON.stringify(action.payload.user));
        }
      })
      .addCase(refreshToken.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
        state.isAuthenticated = false;
      })
      // Update Profile
      .addCase(updateUserProfile.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateUserProfile.fulfilled, (state, action) => {
        state.isLoading = false;
        // Merge the updated profile data with existing user data (using camelCase to match GraphQL schema)
        state.user = {
          ...state.user,
          firstName: action.payload.firstName,
          lastName: action.payload.lastName,
          phoneNumber: action.payload.phoneNumber
        };
        state.error = null;
      })
      .addCase(updateUserProfile.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });
  },
});

export const { clearError, setLoading, setAuthState, clearAuthState } = authSlice.actions;
export default authSlice.reducer;
